// Breadcrumb.tsx
import { Button } from '@insify/design-system/components';
import { IconicArrowLeft, IconicChevronRight } from '@insify/design-system/iconic-icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CenterAlignedContainer } from '../CenterAlignedContainer';

import styles from './styles.module.css';

import type React from 'react';
import type { Params } from 'react-router-dom';

interface BreadcrumbProps {
  className?: string;
  crumbs: { id: string; pathname: string; params: Params<string>; data: unknown; handle: unknown; titleKey: string }[];
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ className = '', crumbs }) => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();

  // we still return an empty container to preserve the space for layout
  const isVisible = crumbs.length > 1;

  const handleBreadcrumbClick = (path: string) => {
    navigate(path);
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <div className={clsx(styles.breadcrumbContainer, className)}>
      <div className={styles.breadcrumbMobile}>
        {isVisible && (
          <Button
            kind='neutral'
            type='button'
            trackingId='breadcrumb-back'
            className={styles.breadcrumbBackBtn}
            onClick={goBack}
            aria-label='Go back'
          >
            <IconicArrowLeft />
          </Button>
        )}
      </div>

      <div className={styles.breadcrumbDesktop}>
        {isVisible && (
          <CenterAlignedContainer>
            <nav aria-label='Breadcrumb'>
              <ol className={styles.breadcrumbList}>
                {crumbs.map((crumb, i) => {
                  const isLast = i === crumbs.length - 1;

                  const { pathname } = crumb;
                  const crumbText = t(crumb.titleKey);

                  return (
                    <li
                      key={`${crumb.pathname}`}
                      className={clsx(styles.breadcrumbItem, isLast && styles.breadcrumbCurrent)}
                    >
                      {isLast ? (
                        <span aria-current='page'>{crumbText}</span>
                      ) : (
                        <>
                          <a
                            href={pathname}
                            onClick={(e) => {
                              e.preventDefault();
                              handleBreadcrumbClick(pathname);
                            }}
                          >
                            {crumbText}
                          </a>
                          <span className='breadcrumb-separator'>
                            <IconicChevronRight />
                          </span>
                        </>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </CenterAlignedContainer>
        )}
      </div>
    </div>
  );
};
